import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { CATEGORIES, TOP_LEVEL_CATEGORIES, listingPath } from "@/lib/constants";
import { Package, Search, Flag, FlagOff, Trash2, RotateCcw, ExternalLink, Sparkles, X, Loader2, AlertTriangle, PackageCheck, Undo2, CheckCircle2, MoreHorizontal, ArrowRight, ClipboardList, Percent } from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";
import { useAuth } from "@/context/AuthContext";
import { hasSuperPrivileges } from "@/lib/accountRole";

// Queue-card palette — the platform's three semantic accents, no new colors.
const AMBER = "#C2600F";   // flagged — needs a look
const GOLD = "#E8C46A";    // in review — awaiting a decision
const OLIVE = "#57682A";   // live — informational

// Compact relative age: "3m" / "5h" / "8d". Empty on a bad/absent timestamp.
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

// Full category path once the tree ships: "Scopes › Red dots". Flat strings render as-is today.
function categoryPath(cat) {
  if (!cat) return "—";
  return String(cat).split(/\s*(?:›|>|\/|::)\s*/).filter(Boolean).join(" › ");
}

// The category rendered by the picker + the one that comes back on a listing must round-trip; the
// dropdown stays keyed on the raw stored string.
function CategoryCrumb({ cat }) {
  return <span className="text-muted-foreground">{categoryPath(cat)}</span>;
}

// Admin promotional fee override on a SINGLE listing — a 0% no-fee promo or a custom rate, applied
// at sale time (it can only ever LOWER the seller's normal fee). Indefinite or a scheduled review
// clock; expiry only FLAGS for a decision, it never silently reverts. Mirrors the per-account
// beta-tester override drawer in Accounts. Backed by POST /admin/platform/listings/{id}/fee-override.
function FeeOverrideModal({ listing, onClose, onSaved }) {
  const active = listing.fee_override && listing.fee_override.active ? listing.fee_override : null;
  const [rate, setRate] = useState(active ? String(active.rate ?? 0) : "0");
  const [mode, setMode] = useState(active ? (active.expires_at ? "scheduled" : "indefinite") : "indefinite");
  const [months, setMonths] = useState("6");
  const [note, setNote] = useState(active?.note || "");
  const [busy, setBusy] = useState(false);

  const expires = active?.expires_at ? new Date(active.expires_at) : null;
  const expiresLabel = expires && !Number.isNaN(expires.getTime())
    ? expires.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
  const r = Math.max(0, Math.min(100, parseFloat(rate) || 0));

  const save = async () => {
    const m = mode === "scheduled" ? Math.max(1, Math.min(60, parseInt(months, 10) || 6)) : 0;
    setBusy(true);
    try {
      await api.post(`/admin/platform/listings/${listing.listing_id}/fee-override`, {
        active: true, rate: r, months: m, note: note.trim() || null, clear_review: true,
      });
      toast.success(`${r <= 0 ? "Zero-fee promo" : `Fee ${r}%`} set${m ? ` · review in ${m} mo` : " · indefinite"}.`);
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/platform/listings/${listing.listing_id}/fee-override`, { active: false });
      toast.success("Fee override cleared — back to the seller's normal rate.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" data-testid="fee-override-modal" onClick={onClose}>
      <div className="bg-surface border border-sniper w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
          <h3 className="font-heading text-xl font-bold uppercase text-foreground flex items-center gap-2"><Percent className="w-5 h-5 text-olive-bright" /> Fee override</h3>
          <button onClick={onClose} data-testid="fee-override-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="font-mono text-[11px] text-secondary-foreground leading-relaxed">
            <div className="text-foreground truncate">{listing.title}</div>
            <div className="text-muted-foreground">@{listing.seller_username || listing.seller_id} · {listing.listing_type === "sell" ? `$${listing.price}` : (listing.listing_type || "—").toUpperCase()}</div>
          </div>

          {active && (
            <div className="border border-olive-bright/40 bg-olive/5 px-3 py-2 font-mono text-[11px] space-y-0.5" data-testid="fee-override-current">
              <div className="flex items-center justify-between"><span className="text-muted-foreground uppercase">Current</span><span className="text-olive-bright">{Number(active.rate ?? 0)}%</span></div>
              <div className="flex items-center justify-between"><span className="text-muted-foreground uppercase">Review</span><span className={active.review_needed ? "text-volt" : "text-foreground"}>{expiresLabel || "Indefinite"}</span></div>
              {active.review_needed && (
                <p className="text-volt flex items-start gap-1.5 pt-1 normal-case"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />Past its review date — still applying at {Number(active.rate ?? 0)}%. Adjust or clear below.</p>
              )}
            </div>
          )}

          <div>
            <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Platform fee rate %</label>
            <input type="number" min={0} max={100} step="0.5" value={rate} onChange={(e) => setRate(e.target.value)}
              data-testid="fee-override-rate-input"
              className="bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm w-28" />
            <p className="mt-1 font-mono text-[9px] uppercase text-muted-foreground/70">0 = no-fee promo (shipping only). This can only LOWER the seller's normal fee, never raise it.</p>
          </div>

          <div>
            <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1.5">Duration</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode("indefinite")} data-testid="fee-override-mode-indefinite"
                className={`font-mono uppercase text-[10px] px-3 py-1.5 border transition-colors ${mode === "indefinite" ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-muted-foreground hover:text-foreground"}`}>Indefinite</button>
              <button type="button" onClick={() => setMode("scheduled")} data-testid="fee-override-mode-scheduled"
                className={`font-mono uppercase text-[10px] px-3 py-1.5 border transition-colors ${mode === "scheduled" ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-muted-foreground hover:text-foreground"}`}>Scheduled review</button>
              {mode === "scheduled" && (
                <input type="number" min={1} max={60} value={months} onChange={(e) => setMonths(e.target.value)}
                  data-testid="fee-override-months-input" title="Months until review"
                  className="bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground font-mono text-xs w-20" />
              )}
            </div>
            <p className="mt-1 font-mono text-[9px] uppercase text-muted-foreground/70">{mode === "indefinite" ? "No clock — surfaced in the fee-override review list so nothing stays at a promo forever." : "Flagged for a decision after the clock; never auto-reverted."}</p>
          </div>

          <div>
            <label className="block font-mono text-[9px] uppercase text-muted-foreground mb-1">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="fee-override-note-input"
              placeholder="e.g. Launch promo for a featured seller"
              className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-xs" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-sniper flex items-center justify-between gap-2">
          {active ? (
            <button onClick={clear} disabled={busy} data-testid="fee-override-clear"
              className="inline-flex items-center gap-1.5 font-mono uppercase text-xs px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 transition-colors disabled:opacity-50">
              <Trash2 className="w-3.5 h-3.5" /> Clear override
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} data-testid="fee-override-cancel" className="font-mono uppercase text-xs px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="fee-override-save"
              className="inline-flex items-center gap-1.5 bg-olive hover:bg-olive-bright text-white px-4 py-2 font-mono uppercase text-xs font-bold disabled:opacity-50 transition-colors">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {active ? "Update" : "Set override"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminListings() {
  const { user: viewer } = useAuth();
  const canSweepApply = hasSuperPrivileges(viewer?.role);  // applying a mass sweep is super-admin only
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the SINGLE SOURCE OF TRUTH for the queue's filters (status + flagged), so the cards,
  // the dropdown, the table, and a shared/deep-linked URL can never disagree — and the same numbers
  // the cards show are exactly what the table returns when you click one.
  const STATUS_OPTIONS = ["active", "pending_review", "sold", "removed", "all"];
  const rawStatus = searchParams.get("status");
  // Default to the LIVE state (ACTIVE) — the useful default is what's on the platform now, not the
  // full history. "all" is an explicit token so "show everything" never collapses to "no choice yet".
  const status = STATUS_OPTIONS.includes(rawStatus) ? rawStatus : "active";
  const flaggedOnly = searchParams.get("flagged") === "1";

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const [sweep, setSweep] = useState(null);        // dry-run { groups:{junk,test_prefix,demo}, ... }
  const [sweepOpen, setSweepOpen] = useState(false);
  const [sweepBusy, setSweepBusy] = useState(false);
  const [brandCleanup, setBrandCleanup] = useState(null); // dry-run { groups:[{from,to,count,samples}], ... }
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandBusy, setBrandBusy] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [opBusy, setOpBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [removeOpen, setRemoveOpen] = useState(false);
  const [batchReason, setBatchReason] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [feeListing, setFeeListing] = useState(null);  // per-listing fee-override drawer target
  const [loadErr, setLoadErr] = useState(null);

  const setStatus = (v) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("status", v);   // always explicit, incl. "all" — keeps the view linkable + reload-safe
      return next;
    });
  };
  // A queue card sets BOTH filters at once so the resulting table matches the card's count exactly.
  // On mobile the filtered table lives far below the fold, so a tap that only rewrites the URL feels
  // like nothing happened — jump to the results (and highlight the active card) for clear feedback.
  const resultsRef = useRef(null);
  const applyQueue = ({ status: s, flagged: fl } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (s) next.set("status", s);
      if (fl) next.set("flagged", "1"); else next.delete("flagged");
      return next;
    });
    requestAnimationFrame(() => {
      if (typeof window !== "undefined" && window.innerWidth < 640) {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };
  const clearFlagged = () => setSearchParams((prev) => {
    const n = new URLSearchParams(prev); n.delete("flagged"); return n;
  });

  // Write the ACTIVE default into the URL so the view is shareable and survives a reload.
  useEffect(() => {
    if (!STATUS_OPTIONS.includes(searchParams.get("status"))) {
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set("status", "active"); return n; }, { replace: true });
    }
  }, [searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set("search", search.trim());
    if (status && status !== "all") p.set("status", status);
    if (category) p.set("category", category);
    if (flaggedOnly) p.set("flagged", "true");
    p.set("limit", "80");
    setLoadErr(null);
    api.get(`/admin/platform/listings?${p.toString()}`).then(({ data }) => { setRows(data.listings); setStats(data.stats); setTotal(data.total); })
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load listings."));
  }, [search, status, category, flaggedOnly]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Selection is scoped to what's on screen — clear it whenever the visible set changes so a
  // stale pick can never be carried across a filter change into records the admin can't see.
  useEffect(() => { setSelected(new Set()); }, [search, status, category, flaggedOnly]);

  const selectableRows = rows.filter((l) => l.status !== "removed");
  const allVisibleSelected = selectableRows.length > 0 && selectableRows.every((l) => selected.has(l.listing_id));
  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAllVisible = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) selectableRows.forEach((l) => n.delete(l.listing_id));
    else selectableRows.forEach((l) => n.add(l.listing_id));
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const overCap = selected.size > 50;
  const batchOk = batchReason.trim().length >= 10 && selected.size > 0 && !overCap;
  const submitBatch = async () => {
    if (!batchOk) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/admin/platform/listings/bulk-remove", { listing_ids: [...selected], reason: batchReason.trim() });
      toast.success(`Removed ${data.removed} listing(s)${data.skipped.length ? ` — ${data.skipped.length} skipped` : ""}.`);
      setRemoveOpen(false); setBatchReason(""); clearSelection(); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBatchBusy(false); }
  };

  const setListingStatus = async (l, newStatus) => {
    if (newStatus === "removed" && !window.confirm(`Remove "${l.title}" from the marketplace?`)) return;
    try {
      await api.post(`/admin/platform/listings/${l.listing_id}/status?status=${newStatus}`);
      toast.success(newStatus === "removed" ? "Listing removed." : "Listing restored.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // Dismiss a moderation flag on an ALREADY-active listing without changing its status (approving a
  // pending_review listing clears the flag on its own; this is for live listings with no approve step).
  const clearFlag = async (l) => {
    try {
      await api.post(`/admin/platform/listings/${l.listing_id}/clear-flag`);
      toast.success("Flag cleared.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const runSweepDryRun = async () => {
    setSweepBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/sweep`, { include_demo: false });
      setSweep(data); setIncludeDemo(false); setSweepOpen(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSweepBusy(false); }
  };

  const applySweep = async () => {
    setSweepBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/sweep?apply=true`, { include_demo: includeDemo });
      toast.success(`Removed ${data.removed_count || 0} listing(s) — undo with "Restore last".`);
      setSweepOpen(false); setSweep(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSweepBusy(false); }
  };

  const runBrandCleanupDryRun = async () => {
    setBrandBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/brand-cleanup`);
      setBrandCleanup(data); setBrandOpen(true);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBrandBusy(false); }
  };

  const applyBrandCleanup = async () => {
    setBrandBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/brand-cleanup?apply=true`);
      toast.success(`Re-canonicalized ${data.updated_count || 0} listing brand(s).`);
      setBrandOpen(false); setBrandCleanup(null); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBrandBusy(false); }
  };

  const runBackfill = async () => {
    setOpBusy(true);
    try {
      const dry = await api.post(`/admin/listings/backfill-sold`);
      if (!dry.data.candidates) { toast.info("No sold-but-active listings to correct."); return; }
      const { data } = await api.post(`/admin/listings/backfill-sold?apply=true`);
      toast.success(`Corrected ${data.fixed_count} sold-but-active listing(s).`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setOpBusy(false); }
  };

  const restoreLast = async () => {
    if (!window.confirm("Restore the most recent sweep/backfill batch?\n\nThis reverts every listing in that batch to its exact prior status. It's a bulk change — proceed?")) return;
    setOpBusy(true);
    try {
      const { data } = await api.post(`/admin/listings/restore-last`);
      toast.success(`Restored ${data.restored} listing(s) from the last ${data.op}.`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setOpBusy(false); }
  };

  // ---- queue state (all from the SAME response the table reads) ----
  const flaggedN = stats?.flagged || 0;
  const reviewN = stats?.pending_review || 0;
  const liveN = stats?.active || 0;
  const needsAttention = flaggedN + reviewN;
  const allClear = stats && flaggedN === 0 && reviewN === 0;
  // Which queue card matches the table's current filter — drives the active highlight.
  const activeCard = flaggedOnly ? "flagged" : status === "pending_review" ? "review" : status === "active" ? "live" : null;

  const menuItem = "w-full flex items-center gap-2 px-3 py-2 font-mono uppercase text-[11px] text-secondary-foreground hover:text-foreground hover:bg-white/[0.03] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left";

  // Rich empty state copy — states what happened + offers the next action (never a bare "no match").
  const isDefaultActiveView = status === "active" && !search.trim() && !category && !flaggedOnly;
  const emptyTitle = isDefaultActiveView ? "No active listings" : "No listings match this view";
  const emptySub = isDefaultActiveView
    ? ((stats?.removed || 0) > 0
        ? `${stats.removed} listing${stats.removed === 1 ? " is" : "s are"} removed. Nothing is live on the marketplace right now.`
        : "Nothing is live on the marketplace right now.")
    : "Adjust the search, status, or category above to widen the view.";

  return (
    <div className={`text-foreground ${selected.size > 0 ? "pb-28" : ""}`} data-testid="admin-listings">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Package className="w-7 h-7 text-olive-bright" /> Listings</h2>
        {stats && (
          <span data-testid="listings-subheader" className="font-mono text-xs uppercase tracking-wide" style={{ color: needsAttention > 0 ? AMBER : undefined }}>
            {needsAttention > 0
              ? <span className="inline-flex items-center gap-1.5" style={{ color: "#E8A05A" }}><AlertTriangle className="w-3.5 h-3.5" /> {needsAttention} need{needsAttention === 1 ? "s" : ""} attention</span>
              : <span className="inline-flex items-center gap-1.5 text-olive-bright"><CheckCircle2 className="w-3.5 h-3.5" /> Nothing needs attention</span>}
          </span>
        )}
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2">Platform-wide listing management — clear the attention queue, then search or filter the table.</p>
      <Crosshair className="my-5" />

      {/* WORK QUEUE — three cards, or one "All clear" row when nothing needs attention */}
      {stats && (allClear ? (
        <div data-testid="listings-all-clear" className="flex items-center gap-2 border border-sniper bg-surface px-4 py-3 mb-6" style={{ borderLeft: `4px solid ${OLIVE}` }}>
          <CheckCircle2 className="w-4 h-4 text-olive-bright shrink-0" />
          <span className="font-mono text-xs text-secondary-foreground">All clear — nothing flagged, nothing in review. <span className="text-foreground">{liveN}</span> live on the marketplace.</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="listings-queue">
          {/* FLAGGED — amber */}
          <button type="button" aria-pressed={activeCard === "flagged"} onClick={() => applyQueue({ status: "active", flagged: true })} data-testid="listings-card-flagged"
            className={`text-left border bg-surface p-4 transition-colors ${activeCard === "flagged" ? "border-[#C2600F] bg-white/[0.05]" : "border-sniper hover:bg-white/[0.02]"}`} style={{ borderLeft: `4px solid ${AMBER}` }}>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide" style={{ color: "#E8A05A" }}><Flag className="w-3.5 h-3.5" /> Flagged</div>
            <div className="font-heading text-3xl mt-1 text-foreground" data-testid="listings-card-flagged-count">{flaggedN}</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground">{flaggedN > 0 && stats.oldest_flagged_at ? `oldest ${timeAgo(stats.oldest_flagged_at)} old` : "—"}</span>
              <span className="font-mono text-[11px] inline-flex items-center gap-1" style={{ color: "#E8A05A" }}>{activeCard === "flagged" ? <><CheckCircle2 className="w-3 h-3" /> showing</> : <>review <ArrowRight className="w-3 h-3" /></>}</span>
            </div>
          </button>
          {/* IN REVIEW — gold */}
          <button type="button" aria-pressed={activeCard === "review"} onClick={() => applyQueue({ status: "pending_review" })} data-testid="listings-card-review"
            className={`text-left border bg-surface p-4 transition-colors ${activeCard === "review" ? "border-[#E8C46A] bg-white/[0.05]" : "border-sniper hover:bg-white/[0.02]"}`} style={{ borderLeft: `4px solid ${GOLD}` }}>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide" style={{ color: GOLD }}><ClipboardList className="w-3.5 h-3.5" /> In review</div>
            <div className="font-heading text-3xl mt-1 text-foreground" data-testid="listings-card-review-count">{reviewN}</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground">awaiting a decision</span>
              <span className="font-mono text-[11px] inline-flex items-center gap-1" style={{ color: GOLD }}>{activeCard === "review" ? <><CheckCircle2 className="w-3 h-3" /> showing</> : <>open <ArrowRight className="w-3 h-3" /></>}</span>
            </div>
          </button>
          {/* LIVE ON MARKETPLACE — olive */}
          <button type="button" aria-pressed={activeCard === "live"} onClick={() => applyQueue({ status: "active" })} data-testid="listings-card-live"
            className={`text-left border bg-surface p-4 transition-colors ${activeCard === "live" ? "border-olive bg-white/[0.05]" : "border-sniper hover:bg-white/[0.02]"}`} style={{ borderLeft: `4px solid ${OLIVE}` }}>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-olive-bright"><Package className="w-3.5 h-3.5" /> Live on marketplace</div>
            <div className="font-heading text-3xl mt-1 text-foreground" data-testid="listings-card-live-count">{liveN}</div>
            <div className="mt-1 font-mono text-[11px] text-muted-foreground" data-testid="listings-card-live-note">of {stats.total} total · {stats.removed} removed, {stats.sold} sold</div>
          </button>
        </div>
      ))}

      {/* FILTER BAR — search, status, category. Destructive tools live in the ⋯ menu, not here. */}
      <div ref={resultsRef} className="flex flex-wrap items-center gap-2 mb-3 scroll-mt-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title / brand / id"
            data-testid="listings-search" className="w-full bg-input border border-sniper focus:border-olive outline-none pl-9 pr-3 py-2.5 text-foreground font-mono text-sm" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="listings-status-filter" className="bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm uppercase">
          <option value="all">All status</option><option value="active">Active</option><option value="pending_review">In review</option><option value="sold">Sold</option><option value="removed">Removed</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="listings-category-filter" className="bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-mono text-sm">
          <option value="">All categories</option>{TOP_LEVEL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="relative ml-auto">
          <button onClick={() => setMenuOpen((v) => !v)} data-testid="listings-more-menu-btn" title="Maintenance tools"
            className={`inline-flex items-center gap-1.5 border px-3 py-2.5 font-mono uppercase text-xs transition-colors ${menuOpen ? "border-olive text-foreground" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div data-testid="listings-more-menu" className="absolute right-0 mt-1 z-50 w-56 bg-surface border border-sniper shadow-2xl py-1">
                <div className="px-3 py-1.5 font-mono text-[9px] uppercase tracking-wide text-muted-foreground border-b border-sniper">Maintenance</div>
                <button onClick={() => { setMenuOpen(false); runSweepDryRun(); }} disabled={sweepBusy} data-testid="listings-sweep-btn" className={menuItem}>
                  {sweepBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-blaze" />} Cleanup sweep
                </button>
                <button onClick={() => { setMenuOpen(false); runBrandCleanupDryRun(); }} disabled={brandBusy} data-testid="listings-brand-cleanup-btn" className={menuItem}>
                  {brandBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5 text-volt" />} Brand cleanup
                </button>
                <button onClick={() => { setMenuOpen(false); runBackfill(); }} disabled={opBusy} data-testid="listings-backfill-btn" className={menuItem}>
                  {opBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5 text-olive-bright" />} Backfill sold
                </button>
                <button onClick={() => { setMenuOpen(false); restoreLast(); }} disabled={opBusy} data-testid="listings-restore-last-btn" className={menuItem}>
                  <Undo2 className="w-3.5 h-3.5" /> Restore last
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Active-filter chips (flagged is queue-driven, not in the bar — keep a way to clear it) */}
      {flaggedOnly && (
        <div className="mb-3" data-testid="listings-active-chips">
          <button onClick={clearFlagged} data-testid="listings-chip-flagged"
            className="inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono uppercase text-[11px] transition-colors" style={{ borderColor: AMBER, color: "#E8A05A" }}>
            <Flag className="w-3 h-3" /> Flagged only <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* TABLE — never renders headers above zero rows; a rich empty state stands in instead */}
      {loadErr ? (
        <LoadFailed reason={loadErr} onRetry={load} testid="listings-load-failed" />
      ) : rows.length === 0 ? (
        <div className="border border-sniper bg-surface p-10 text-center" data-testid="listings-empty">
          <Package className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="font-heading text-lg uppercase text-foreground mt-3">{emptyTitle}</p>
          <p className="font-mono text-xs text-secondary-foreground mt-1 max-w-md mx-auto">{emptySub}</p>
          <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
            {(stats?.removed || 0) > 0 && status !== "removed" && (
              <button onClick={() => applyQueue({ status: "removed" })} data-testid="listings-empty-view-removed"
                className="inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground px-4 py-2 font-mono uppercase text-xs transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> View removed ({stats.removed})
              </button>
            )}
            {!isDefaultActiveView && (
              <button onClick={() => { setSearch(""); setCategory(""); applyQueue({ status: "active" }); }} data-testid="listings-empty-clear"
                className="inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground px-4 py-2 font-mono uppercase text-xs transition-colors">
                <RotateCcw className="w-3.5 h-3.5" /> Back to active
              </button>
            )}
            <button onClick={restoreLast} disabled={opBusy} data-testid="listings-empty-restore-sweep"
              className="inline-flex items-center gap-1.5 border border-olive/50 text-olive-bright hover:bg-olive/10 px-4 py-2 font-mono uppercase text-xs transition-colors disabled:opacity-50">
              {opBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />} Restore last sweep
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-sniper bg-surface overflow-x-auto" data-testid="listings-table">
          <table className="w-full font-mono text-xs">
            <thead><tr className="text-left text-muted-foreground uppercase text-[10px] border-b border-sniper">
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} disabled={selectableRows.length === 0}
                  data-testid="listings-select-all" aria-label="Select all visible rows" className="accent-blaze w-4 h-4 align-middle" />
              </th>
              <th className="px-3 py-2">Listing</th><th className="px-3 py-2">Seller</th><th className="px-3 py-2">Price</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody className="divide-y divide-sniper">
              {rows.map((l) => (
                <tr key={l.listing_id} className="text-secondary-foreground" data-testid={`listing-row-${l.listing_id}`}>
                  <td className="px-3 py-1.5 w-8">
                    {l.status !== "removed" && (
                      <input type="checkbox" checked={selected.has(l.listing_id)} onChange={() => toggleRow(l.listing_id)}
                        data-testid={`listing-select-${l.listing_id}`} aria-label={`Select ${l.title}`} className="accent-blaze w-4 h-4 align-middle" />
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 shrink-0 border border-sniper bg-obsidian overflow-hidden">{l.photo && <img src={l.photo} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="min-w-0 leading-tight">
                        <Link to={listingPath(l)} target="_blank" className="text-foreground hover:text-volt truncate block max-w-[260px]">{l.title}</Link>
                        <div className="flex items-center gap-1.5 text-[11px]">
                          <CategoryCrumb cat={l.category} />
                          {l.flagged && <span className="text-[9px] uppercase text-blaze border border-blaze/50 px-1">flagged</span>}
                          {l.is_demo && <span className="text-[9px] uppercase text-volt border border-volt/50 px-1">demo</span>}
                          {l.fee_override && l.fee_override.active && (
                            <span data-testid={`listing-fee-chip-${l.listing_id}`} title={`Admin fee override · ${Number(l.fee_override.rate ?? 0)}%${l.fee_override.expires_at ? "" : " · indefinite"}`}
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase text-olive-bright border border-olive-bright/50 px-1">
                              <Percent className="w-2.5 h-2.5" />{Number(l.fee_override.rate ?? 0)}% fee
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-1.5"><span className="inline-flex items-center gap-2">@{l.seller_username || "?"} <RoleTag kind={l.seller_kind} /></span></td>
                  <td className="px-3 py-1.5">{l.listing_type === "sell" ? `$${l.price}` : (l.listing_type || "—").toUpperCase()}</td>
                  <td className="px-3 py-1.5"><span className={l.status === "active" ? "text-olive-bright" : l.status === "pending_review" ? "text-volt" : l.status === "sold" ? "text-blaze" : "text-muted-foreground"}>{l.status === "pending_review" ? "in review" : l.status}</span></td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <Link to={listingPath(l)} target="_blank" className="p-1.5 inline-block text-muted-foreground hover:text-foreground"><ExternalLink className="w-3.5 h-3.5" /></Link>
                    {l.listing_type === "sell" && l.status !== "removed" && (
                      <button onClick={() => setFeeListing(l)} data-testid={`listing-fee-override-${l.listing_id}`}
                        title={l.fee_override?.active ? `Fee override active · ${Number(l.fee_override.rate ?? 0)}%` : "Set a promotional fee override"}
                        className={`p-1.5 ${l.fee_override?.active ? "text-olive-bright" : "text-muted-foreground hover:text-olive-bright"}`}><Percent className="w-3.5 h-3.5" /></button>
                    )}
                    {l.status === "pending_review" && (
                      <button onClick={() => setListingStatus(l, "active")} data-testid={`listing-approve-${l.listing_id}`} title="Approve — publish to the feed (clears the flag)" className="p-1.5 text-muted-foreground hover:text-olive-bright"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                    )}
                    {l.flagged && l.status !== "pending_review" && l.status !== "removed" && (
                      <button onClick={() => clearFlag(l)} data-testid={`listing-clear-flag-${l.listing_id}`} title="Clear flag — dismiss moderation flag, keep it live" className="p-1.5 text-muted-foreground hover:text-olive-bright"><FlagOff className="w-3.5 h-3.5" /></button>
                    )}
                    {l.status === "removed"
                      ? <button onClick={() => setListingStatus(l, "active")} data-testid={`listing-restore-${l.listing_id}`} className="p-1.5 text-muted-foreground hover:text-olive-bright"><RotateCcw className="w-3.5 h-3.5" /></button>
                      : <button onClick={() => setListingStatus(l, "removed")} data-testid={`listing-remove-${l.listing_id}`} className="p-1.5 text-muted-foreground hover:text-blaze"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && !loadErr && (
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-2" data-testid="listings-shown-count">{total} listing{total === 1 ? "" : "s"} shown</p>
      )}

      {/* Sticky bulk-action bar — appears only on selection, shows the exact count */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-surface border-t-2 border-blaze/60 shadow-2xl px-4 py-3 flex items-center justify-between gap-3" data-testid="listings-bulk-bar">
          <span className="font-mono text-xs uppercase text-foreground flex items-center gap-2">
            <span className="text-blaze font-bold" data-testid="listings-bulk-count">{selected.size}</span> selected
            {overCap && <span className="text-blaze normal-case">— over the 50 cap, deselect {selected.size - 50}</span>}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={clearSelection} data-testid="listings-bulk-clear" className="font-mono uppercase text-xs px-3 py-2 border border-sniper text-muted-foreground hover:text-foreground transition-colors">Clear</button>
            <button onClick={() => setRemoveOpen(true)} disabled={overCap} data-testid="listings-bulk-remove-open"
              className="inline-flex items-center gap-1.5 font-mono uppercase text-xs px-4 py-2 border border-blaze/60 text-blaze hover:bg-blaze/10 font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Trash2 className="w-3.5 h-3.5" /> Remove selected
            </button>
          </div>
        </div>
      )}

      {/* Batch-remove reason modal — one reason for the whole batch, min 10 chars */}
      {removeOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4" data-testid="listings-bulk-modal" onClick={() => setRemoveOpen(false)}>
          <div className="bg-surface border border-sniper w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
              <h3 className="font-heading text-xl font-bold uppercase text-foreground flex items-center gap-2"><Trash2 className="w-5 h-5 text-blaze" /> Remove {selected.size} listing{selected.size === 1 ? "" : "s"}</h3>
              <button onClick={() => setRemoveOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="font-mono text-[11px] text-secondary-foreground leading-relaxed">Soft delete to <span className="text-foreground">REMOVED</span> (recoverable). One reason applies to the whole batch — if it isn't the same reason for every listing, they aren't one batch. Each listing still gets its own audit record with a shared batch id.</p>
              <textarea value={batchReason} onChange={(e) => setBatchReason(e.target.value)} rows={3} data-testid="listings-bulk-reason"
                placeholder="Reason for removing this batch (min 10 chars) — recorded per listing in Audit Events"
                className="w-full bg-input border border-sniper focus:border-blaze outline-none px-3 py-2 text-foreground text-sm" />
              {batchReason.trim().length < 10 && <p className="text-[10px] text-blaze font-mono">Reason must be at least 10 characters ({batchReason.trim().length}/10).</p>}
            </div>
            <div className="px-5 py-4 border-t border-sniper flex items-center justify-end gap-2">
              <button onClick={() => setRemoveOpen(false)} data-testid="listings-bulk-cancel" className="font-mono uppercase text-xs px-4 py-2 border border-sniper text-muted-foreground hover:text-foreground">Cancel</button>
              <button onClick={submitBatch} disabled={!batchOk || batchBusy} data-testid="listings-bulk-confirm"
                className="inline-flex items-center gap-1.5 bg-blaze text-black hover:bg-blaze/90 px-4 py-2 font-mono uppercase text-xs font-bold disabled:opacity-50">
                {batchBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Remove {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {sweepOpen && sweep && (() => {
        const g = sweep.groups || { junk: { count: 0, rows: [] }, test_prefix: { count: 0, rows: [] }, demo: { count: 0, rows: [] } };
        const sweepTotal = g.junk.count + g.test_prefix.count + (includeDemo ? g.demo.count : 0);
        const GROUP_META = {
          junk: { label: "Junk", desc: "emoji/empty titles, absurd prices, injected locations", cls: "text-blaze border-blaze/50" },
          test_prefix: { label: "Test data", desc: "TEST_/DEMO_ titles or test accounts", cls: "text-volt border-volt/50" },
          demo: { label: "Demo seed", desc: "the demo accounts — kept unless you opt in", cls: "text-steel border-steel/50" },
        };
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="sweep-modal" onClick={() => setSweepOpen(false)}>
          <div className="bg-surface border border-sniper w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
              <h3 className="font-heading text-xl font-bold uppercase text-foreground flex items-center gap-2"><Sparkles className="w-5 h-5 text-blaze" /> Marketplace sweep</h3>
              <button onClick={() => setSweepOpen(false)} data-testid="sweep-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <p className="font-mono text-xs text-secondary-foreground">
                Removable listings grouped by reason. This is a <span className="text-foreground uppercase">dry run</span> — nothing is changed yet. Removal is a soft delete to <span className="text-foreground">REMOVED</span> (recoverable with "Restore last").
              </p>
              {["junk", "test_prefix", "demo"].map((key) => {
                const grp = g[key]; const meta = GROUP_META[key];
                const active = key !== "demo" || includeDemo;
                return (
                  <div key={key} className={`mt-3 border ${active ? "border-sniper" : "border-sniper/40 opacity-60"}`} data-testid={`sweep-group-${key}`}>
                    <div className="flex items-center justify-between px-3 py-2 border-b border-sniper">
                      <div className="font-mono text-xs">
                        <span className={`uppercase border px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
                        <span className="text-muted-foreground ml-2">{meta.desc}</span>
                      </div>
                      <span className="font-heading text-lg text-foreground" data-testid={`sweep-count-${key}`}>{grp.count}</span>
                    </div>
                    {key === "demo" && (
                      <label className="flex items-center gap-2 px-3 py-2 border-b border-sniper font-mono text-[11px] text-secondary-foreground cursor-pointer" data-testid="sweep-include-demo-label">
                        <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} data-testid="sweep-include-demo" className="accent-blaze w-4 h-4" />
                        Also remove demo seed listings (off by default — this deletes the demo@ sales data)
                      </label>
                    )}
                    {grp.count > 0 && (
                      <div className="divide-y divide-sniper max-h-40 overflow-y-auto">
                        {grp.rows.map((o) => (
                          <div key={o.listing_id} className="px-3 py-2 flex items-center justify-between gap-3 font-mono text-[11px]">
                            <div className="min-w-0">
                              <div className="text-foreground truncate max-w-[320px]">{o.title || <span className="italic text-muted-foreground">(empty title)</span>}</div>
                              <div className="text-muted-foreground">@{o.seller_username || o.seller_id} · ${String(o.price)} · {o.status}</div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              {(o.reasons || (o.reason ? [o.reason] : [])).map((r) => <span key={r} className="text-[9px] uppercase text-blaze border border-blaze/50 px-1 py-0.5">{r.replace("bad_", "")}</span>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="px-5 py-4 border-t border-sniper flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-blaze" /> {canSweepApply ? "Super-admin. Soft delete — recoverable." : "Dry run only — applying a sweep is super-admin only."}</span>
              <div className="flex gap-2">
                <button onClick={() => setSweepOpen(false)} data-testid="sweep-cancel" className="border border-sniper text-secondary-foreground hover:text-foreground px-4 py-2 font-mono uppercase text-xs">{canSweepApply ? "Cancel" : "Close"}</button>
                <button onClick={applySweep} disabled={!canSweepApply || sweepBusy || sweepTotal === 0} data-testid="sweep-apply"
                  title={canSweepApply ? undefined : "Applying a sweep requires a super-admin account"}
                  className="bg-blaze text-black hover:bg-blaze/90 px-4 py-2 font-mono uppercase text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                  {sweepBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {canSweepApply ? `Remove ${sweepTotal}` : "Super-admin only"}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {brandOpen && brandCleanup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="brand-cleanup-modal" onClick={() => setBrandOpen(false)}>
          <div className="bg-surface border border-sniper w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-sniper">
              <h3 className="font-heading text-xl font-bold uppercase text-foreground flex items-center gap-2"><ClipboardList className="w-5 h-5 text-volt" /> Brand cleanup</h3>
              <button onClick={() => setBrandOpen(false)} data-testid="brand-cleanup-close" className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <p className="font-mono text-xs text-secondary-foreground">
                Listing brands that don't match the canonical registry spelling. This is a <span className="text-foreground uppercase">dry run</span> — nothing is changed yet. Applying rewrites <span className="text-foreground">listing.brand</span> to the canonical form (idempotent; niche brands the registry doesn't know are left alone).
              </p>
              <div className="mt-3 flex items-center gap-4 font-mono text-xs">
                <span className="text-muted-foreground">Scanned <span className="text-foreground" data-testid="brand-cleanup-scanned">{brandCleanup.total_scanned}</span></span>
                <span className="text-muted-foreground">Will change <span className="text-volt" data-testid="brand-cleanup-count">{brandCleanup.change_count}</span></span>
              </div>
              {brandCleanup.change_count === 0 ? (
                <div className="mt-4 border border-sniper px-4 py-6 text-center font-mono text-sm text-muted-foreground" data-testid="brand-cleanup-clean">
                  Every listing brand already matches the registry. Nothing to do.
                </div>
              ) : (
                <div className="mt-3 border border-sniper divide-y divide-sniper" data-testid="brand-cleanup-groups">
                  {(brandCleanup.groups || []).map((g) => (
                    <div key={`${g.from}->${g.to}`} className="px-3 py-2.5 flex items-center justify-between gap-3 font-mono text-xs" data-testid={`brand-cleanup-group-${g.to}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-muted-foreground line-through truncate max-w-[160px]">{g.from}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-volt shrink-0" />
                        <span className="text-foreground truncate max-w-[160px]">{g.to}</span>
                      </div>
                      <span className="text-volt shrink-0" data-testid={`brand-cleanup-group-count-${g.to}`}>{g.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-sniper flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-muted-foreground flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-volt" /> {canSweepApply ? "Super-admin. Idempotent — safe to re-run." : "Dry run only — applying is super-admin only."}</span>
              <div className="flex gap-2">
                <button onClick={() => setBrandOpen(false)} data-testid="brand-cleanup-cancel" className="border border-sniper text-secondary-foreground hover:text-foreground px-4 py-2 font-mono uppercase text-xs">{canSweepApply ? "Cancel" : "Close"}</button>
                <button onClick={applyBrandCleanup} disabled={!canSweepApply || brandBusy || brandCleanup.change_count === 0} data-testid="brand-cleanup-apply"
                  title={canSweepApply ? undefined : "Applying requires a super-admin account"}
                  className="bg-volt text-black hover:bg-volt/90 px-4 py-2 font-mono uppercase text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                  {brandBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {canSweepApply ? `Apply ${brandCleanup.change_count}` : "Super-admin only"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {feeListing && (
        <FeeOverrideModal listing={feeListing} onClose={() => setFeeListing(null)}
          onSaved={() => { setFeeListing(null); load(); }} />
      )}
    </div>
  );
}
